(() => {
  if (customElements.get("bcs-ingredient-formula")) return;

  class BcsIngredientFormula extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slider = this.querySelector("[data-ingredient-slider]");
      this.track = this.querySelector("[data-ingredient-track]");
      this.cards = [...this.querySelectorAll("[data-ingredient-card]")];
      if (!this.slider || !this.cards.length) return;

      this.motion = matchMedia("(prefers-reduced-motion: reduce)");
      this.cssAnchors = CSS.supports("anchor-name: --bcs-ingredient");
      this.querySelectorAll(".bcs-ingredient-formula__more-content").forEach(
        (content) => {
          if (typeof content.showPopover === "function")
            content.setAttribute("popover", "manual");
        },
      );
      this.motion.addEventListener(
        "change",
        () => {
          if (this.swiper)
            this.swiper.params.speed = this.motion.matches ? 0 : 400;
        },
        { signal },
      );
      this.addEventListener(
        "toggle",
        (event) => {
          if (!event.target.matches(".bcs-ingredient-formula__more")) return;
          const content = event.target.querySelector("[popover]");
          if (!content) return;
          if (event.target.open) {
            content.showPopover();
            if (this.cssAnchors)
              requestAnimationFrame(() => this.alignPopupArrow(event.target));
            else this.positionPopup(event.target);
          } else content.hidePopover();
        },
        { capture: true, signal },
      );
      if (!this.cssAnchors) {
        // Older browsers use a single position when opened, then close on scroll.
        document.addEventListener("scroll", () => this.closePopups(), {
          capture: true,
          passive: true,
          signal,
        });
      }
      window.addEventListener("resize", () => this.closePopups(), { signal });
      this.addEventListener(
        "focusin",
        (event) => {
          const card = event.target.closest("[data-ingredient-card]");
          if (card) this.revealCard(card);
        },
        { signal },
      );
      document.addEventListener(
        "shopify:block:select",
        (event) => {
          const index = this.cards.findIndex(
            (card) => card.dataset.blockId === event.detail.blockId,
          );
          if (index >= 0) this.revealCard(this.cards[index], 0);
        },
        { signal },
      );
      document.addEventListener(
        "shopify:section:unload",
        (event) => {
          if (event.target.contains(this)) this.disconnectedCallback();
        },
        { signal },
      );

      if (typeof window.Swiper === "function") this.initializeSlider();
      else
        window.addEventListener("load", () => this.initializeSlider(), {
          once: true,
          signal,
        });
    }

    initializeSlider() {
      if (this.swiper || typeof window.Swiper !== "function") return;
      this.columns = [];
      for (let index = 0; index < this.cards.length; index += 2) {
        const column = document.createElement("div");
        column.className = "bcs-ingredient-formula__column swiper-slide";
        column.append(...this.cards.slice(index, index + 2));
        this.columns.push(column);
      }
      this.track.replaceChildren(...this.columns);
      this.track.classList.add("is-columns");
      const updateControls = (swiper) => {
        this.querySelector("[data-ingredient-controls]").hidden =
          swiper.isLocked;
        this.querySelector("[data-ingredient-counter]").textContent =
          `${swiper.snapIndex + 1} / ${swiper.snapGrid.length}`;
      };
      this.swiper = new window.Swiper(this.slider, {
        // Native scroll with snap points: smoother swiping on phones than
        // Swiper's JavaScript drag, which fought the page's vertical scroll.
        cssMode: true,
        slidesPerView: 2,
        slidesPerGroup: 1,
        spaceBetween: 12,
        breakpoints: {
          750: { slidesPerView: 3, spaceBetween: 20 },
          990: { slidesPerView: 4, spaceBetween: 20 },
        },
        speed: this.motion.matches ? 0 : 400,
        watchOverflow: true,
        grabCursor: true,
        navigation: {
          prevEl: this.querySelector("[data-ingredient-prev]"),
          nextEl: this.querySelector("[data-ingredient-next]"),
        },
        a11y: {
          prevSlideMessage: this.querySelector(
            "[data-ingredient-prev]",
          ).getAttribute("aria-label"),
          nextSlideMessage: this.querySelector(
            "[data-ingredient-next]",
          ).getAttribute("aria-label"),
          slideLabelMessage: this.dataset.ingredientColumnLabel
            .replace("[index]", "{{index}}")
            .replace("[length]", "{{slidesLength}}"),
        },
        on: {
          init: updateControls,
          snapIndexChange: updateControls,
          snapGridLengthChange: updateControls,
          lock: updateControls,
          unlock: updateControls,
          slideChange: () => this.closePopups(),
          sliderFirstMove: () => this.closePopups(),
        },
      });
    }

    revealCard(card, speed = this.swiper?.params.speed) {
      if (!this.swiper) return;
      const column = this.columns.indexOf(card.parentElement);
      if (column < 0) return;
      const first = this.swiper.activeIndex;
      const visible = this.swiper.params.slidesPerView;
      if (column < first) this.swiper.slideTo(column, speed);
      else if (column >= first + visible)
        this.swiper.slideTo(column - visible + 1, speed);
    }

    alignPopupArrow(detail) {
      const content = detail.querySelector("[popover]");
      if (!content?.matches(":popover-open")) return;
      const icon = detail.querySelector("summary svg").getBoundingClientRect();
      content.style.setProperty(
        "--bcs-ingredient-tooltip-arrow",
        `${icon.left + icon.width / 2 - content.getBoundingClientRect().left - content.clientLeft}px`,
      );
    }

    positionPopup(detail) {
      const content = detail.querySelector("[popover]");
      if (!content?.matches(":popover-open")) return;
      const icon =
        detail.querySelector("summary svg") || detail.querySelector("summary");
      const iconRect = icon.getBoundingClientRect();
      const width = content.offsetWidth;
      const height = content.offsetHeight;
      const center = iconRect.left + iconRect.width / 2;
      const viewportWidth = document.documentElement.clientWidth;
      const left = Math.max(
        12,
        Math.min(center - width / 2, viewportWidth - width - 12),
      );
      content.style.setProperty("--bcs-ingredient-tooltip-left", `${left}px`);
      content.style.setProperty(
        "--bcs-ingredient-tooltip-top",
        `${iconRect.top - height - 10}px`,
      );
      content.style.setProperty(
        "--bcs-ingredient-tooltip-arrow",
        `${center - left - content.clientLeft}px`,
      );
    }

    closePopups() {
      this.querySelectorAll(".bcs-ingredient-formula__more[open]").forEach(
        (detail) => {
          detail.open = false;
          const content = detail.querySelector("[popover]");
          if (content?.matches(":popover-open")) content.hidePopover();
        },
      );
    }

    disconnectedCallback() {
      this.closePopups();
      this.controller?.abort();
      this.swiper?.destroy(true, true);
      if (this.track && this.cards) {
        this.track.replaceChildren(...this.cards);
        this.track.classList.remove("is-columns");
      }
      this.querySelector("[data-ingredient-controls]")?.setAttribute(
        "hidden",
        "",
      );
      this.controller = null;
      this.swiper = null;
    }
  }

  customElements.define("bcs-ingredient-formula", BcsIngredientFormula);
  document.addEventListener("shopify:section:load", (event) => {
    event.target
      .querySelectorAll("bcs-ingredient-formula")
      .forEach((component) => {
        component.connectedCallback();
      });
  });
})();
